import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useViewMode } from '@/stores/viewMode'
import { useNavigate } from 'react-router-dom'
import { useSupplierAdSchedules } from '@/hooks/useSupplierAdSchedules'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { Eye } from 'lucide-react'

/**
 * Supplier-side ad-schedule list (read-only).
 *
 * Shows templates that contain at least one ad from this supplier. Clicking a
 * row opens the detail view (`ViewTemplatePage`), which renders batches with
 * only this supplier's items and BE-computed expected play times.
 *
 * Source data: GET /api/ad-supplier/:supplier_id/ad-schedule-templates
 */
export default function SupplierAdSchedulesPage() {
  const { id: supplierId } = useViewMode()
  const navigate = useNavigate()

  const { templates, isLoading, isError } = useSupplierAdSchedules({ supplierId })

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入廣告排程列表失敗，請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-[240px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">廣告排程</h2>
        <p className="text-muted-foreground mt-2">
          檢視旗下廣告所在的排程模版及預計播放時間（唯讀）
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>排程模版列表</CardTitle>
          <CardDescription>
            僅顯示包含本供應商廣告的模版，共 {templates.length} 筆
          </CardDescription>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              目前沒有任何包含本供應商廣告的排程模版
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>模版名稱</TableHead>
                  <TableHead>說明</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>建立時間</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.template_id}>
                    <TableCell className="font-medium">{t.template_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {t.description || '—'}
                    </TableCell>
                    <TableCell>
                      {t.is_active ? (
                        <Badge className="bg-green-500">啟用</Badge>
                      ) : (
                        <Badge variant="secondary">停用</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(t.created_at), 'yyyy/MM/dd HH:mm', { locale: zhTW })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(`/ad-supplier/${supplierId}/ad-schedules/${t.template_id}`)
                        }
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        查看
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
